<template>
  <div>
    <!-- Header -->
    <div class="flex items-center justify-center border-b pt-0 pb-3">
      <h3 id="tiers.title" class="mb-0 text-center w-full">{{ translate('Loyalty Tiers') }}</h3>
    </div>

    <!-- New Tier Button -->
    <v2-base-button id="tiers.addnewtier" @click="openCreateModal"
      class="mb-3 flex items-center gap-2 bg-primary text-white px-4 py-2 rounded shadow">
      <i class="ni ni-fat-add"></i>
      {{ translate('NEW TIER') }}
    </v2-base-button>

    <!-- Table -->
    <BaseTable id="tiers.table" :headers="tableHeaders" :rows="tableRows" :loading="loading"
      @row-click="handleRowClick">
      <template #cell-name="{ row }">
        <span>English: {{ row.name['en-us'] }}</span><br>
        <span>Arabic: {{ row.name['ar-sa'] }}</span>
      </template>
      <template #cell-action="{ row }">
        <button
          class="px-3 py-1 rounded text-white bg-blue-500 hover:bg-blue-600 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          :class="{ 'bg-gray-400': row.customerCount < 1 || exporting[row.id] }"
          :disabled="row.customerCount < 1 || exporting[row.id]" @click.stop="exportCustomers(row.action)">
          <span v-if="exporting[row.id]">{{ translate('Exporting...') }}</span>
          <span v-else>{{ translate('Export Customers') }}</span>
        </button>
      </template>
    </BaseTable>

    <TierSingleModal id="tiers.modal" v-model="showCreateModal" :tier="selectedTier" @update:tier="updateTier"
      @add:tier="addTier" @tiers-changed="getTiers" />
  </div>
</template>

<script>
import { Loyalty as LoyaltyService } from '@/services/SOLO';
import TierSingleModal from './TierSingleModal.vue';
import translations from '@/mixins/translations';
import notificationAlerts from '@/mixins/notification-alerts';
import { mapGetters } from 'vuex';
import V2BaseButton from '@/components/V2/V2BaseButton.vue';
import BaseTable from '@/components/V2/BaseTable.vue';

export default {
  name: 'LoyaltyTierList',
  components: {
    TierSingleModal,
    V2BaseButton,
    BaseTable,
  },
  mixins: [translations, notificationAlerts],
  data () {
    return {
      showCreateModal: false,
      loading: false,
      tiers: [],
      exporting: {},
      selectedTier: null,
    };
  },
  computed: {
    ...mapGetters({
      getConceptSettings: 'account/getConceptSettings',
    }),
    tableHeaders () {
      return [
        { key: 'id', label: this.translate('ID') },
        { key: 'name', label: this.translate('Name') },
        { key: 'pointsRange', label: this.translate('Points Range') },
        { key: 'collectionRules', label: this.translate('Collection Rules') },
        { key: 'earningRules', label: this.translate('Earning Rules') },
        { key: 'customerCount', label: this.translate('Customer Count') },
        { key: 'action', label: this.translate('Action') },
      ];
    },
    tableRows () {
      return this.tiers.map((tier) => ({
        id: tier.id,
        pointsRange: `${tier['start-point']} - ${tier['end-point']}`,
        collectionRules: `Collect ${tier['collect-rules']['earn-point']} Points per ${this.$displayCurrency(this.getConceptSettings.attributes['currency-code'])} ${tier['collect-rules']['per-sar']} `,
        earningRules: `Earn ${this.$displayCurrency(this.getConceptSettings.attributes['currency-code'])} ${tier['redeem-rules']['earn-sar']}  per ${tier['redeem-rules']['per-point']} Points`,
        customerCount: tier['customer-count'],
        action: tier,
        ...tier,
      }));
    },
  },
  methods: {
    openCreateModal () {
      this.showCreateModal = true;
      this.selectedTier = null;
    },
    handleRowClick (row) {
      this.selectedTier = row;
      this.showCreateModal = true;
    },
    async getTiers () {
      try {
        this.loading = true;
        const tiers = await LoyaltyService.getTiers({
          limit: 'none',
          with_customer_count: true,
          name_translation: true,
        });
        this.tiers = tiers.data.data;
      } catch (error) {
        console.error('Error in getting tiers', error);
      } finally {
        this.loading = false;
      }
    },
    updateTier (tier) {
      this.selectedTier = tier;
    },
    addTier (tier) {
      this.tiers.push(tier);
    },
    async exportCustomers (row) {
      try {
        this.$set(this.exporting, row.id, true);
        const { data } = await LoyaltyService.exportTierCustomers(row.id);
        this.successNotification('Export customers successfully', data.message);
      } catch (error) {
        this.systemErrorNotification('Error in exporting customer');
      } finally {
        this.$set(this.exporting, row.id, false);
      }
    },
  },
  mounted () {
    this.getTiers();
  },
};
</script>
